import { Grid, RenderControl } from '@fareye/ui'
import { responseParser } from '@fareye/utils'
import {
  isEmpty,
  cancelToken,
  get,
  purifySearchParams,
  uniqBy
} from '@fareye/utils'
import queryString from 'query-string'
import React from 'react'
import { Header } from '../../components/Header'
import { ITripDetails, ITab, TripPoiProps } from '../../utility/types'
import { IssueListing } from '@fareye/issue-resolution'
import TripTabDetails from './containers/TripTabDetails'
import { useTranslation } from '@fareye/common'

export const TripDetailContext: any = React.createContext(null)

const initialMapState = {
  data: {
    trip: [],
    issuesCount: 0,
    location: [],
    histories: [],
    stoppages: [],
    overspeedInstances: [],
    loadingData: [],
    unloadingData: [],
    exceptions: [],
    idealPath: [],
    issues: [],
    trackingProviders: []
  },
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: ''
}

export const TripDetails = (props: ITripDetails) => {
  const {
    timer,
    match,
    mapKey,
    config,
    history,
    location,
    routesConfig,
    defaultBaseLayer,
    aerisLayer
  } = props
  let {
    styling: { layoutStyle, style, className },
    issueRaisedConfig,
    showTimezone = false,
    moveMarker = false,
    apiBasedDailyDistanceGraph = false,
    listItems = []
  } = config
  const guarenteedListItems = [
    { key: 'invoiceNo', label: 'Invoice Number' },
    { key: 'referenceNo', label: 'Reference Number' }
  ]
  listItems = uniqBy([...guarenteedListItems, ...listItems], x => x.key)
  const source = cancelToken().source()
  const searchParams = queryString.parse(location.search)
  const hasAuthToken = localStorage.getItem('@Fareye:AuthToken')
  const {
    plant = '',
    tab = 'tripDetails',
    issueRaised,
    debug = 1
  } = searchParams
  const { id: slug }: any = match.params
  const [showDrawer, setShowDrawer] = React.useState(issueRaised ? true : false)
  const hasData = React.useRef(false)
  const [trackingProvider, setTrackingProvider] = React.useState('')
  const [apiResponse, setApiResponse] = React.useState(initialMapState)
  const { t } = useTranslation()
  const [tripPois, setTripPois] = React.useState<TripPoiProps[]>([])

  const [otherFilters, setOtherFilters] = React.useState({
    exceptions: [],
    stoppages: []
  })
  const [selectedIds, setSelectedIds] = React.useState({
    stoppageId: 0,
    overspeedInstanceId: 0,
    locationId: 0,
    exceptionId: 0
  })

  React.useEffect(() => {
    if (debug) {
      fetchTripPois()
    } else {
      setTripPois([])
    }
  }, [debug, timer])

  async function fetchTripPois() {
    try {
      let url = config.tripPoisURL.replace('__slug__', slug)
      if (!hasAuthToken) {
        if (config.tripPoisUnAuthURL) {
          url = `${config.tripPoisUnAuthURL}?slug=${slug}`
        } else {
          return
        }
      }
      const response = await get(url, source.token, {
        headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true }
      })
      if (response?.data?.pois?.length > 0) {
        const newTripPois = response.data.pois.map((poi: any) => ({
          latitude: poi.latitude,
          longitude: poi.longitude,
          radius: poi.radius,
          poiNickName: poi.poiNickName,
          poiActName: poi.poiActName,
          poiType: poi.poiType,
          poiBounds: poi.poiBounds,
          isManual: poi.isManual
        }))
        setTripPois(newTripPois)
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchData() {
    if (!hasData.current) {
      setApiResponse({ ...apiResponse, isLoading: true })
    }
    try {
      let queryParam = `?slug=${slug}`
      if (purifySearchParams(searchParams)) {
        queryParam = `${queryParam}&${purifySearchParams(searchParams)}`
      }
      if (trackingProvider) {
        queryParam = `${queryParam}&tracking_provider=${trackingProvider}`
      }
      const response = await get(
        `${config.tripHistoryURl}${queryParam}`,
        source.token
      )
      const { error, data, message } = responseParser(response)
      let { content } = data

      if (error) {
        setApiResponse({
          ...apiResponse,
          isError: true,
          isSuccess: false,
          isLoading: false,
          message
        })
      } else {
        const {
          issuesCount,
          trip,
          location,
          histories,
          idealHistories,
          trackingProviders
        } = content[0]?.transitView?.[0] || {}
        hasData.current = true
        setApiResponse(prev => ({
          ...prev,
          isError: false,
          data: {
            ...prev.data,
            trip: trip ? trip : {},
            location: location ? location : {},
            histories: histories ? histories : [],
            idealPath: idealHistories ? idealHistories : [],
            trackingProviders: trackingProviders ? trackingProviders : [],
            ...(trackingProvider ? {} : {
              issuesCount: issuesCount ? issuesCount : 0,
              issues: content[0].issues
            })
          },
          isSuccess: true,
          isLoading: false,
          message: !isEmpty(content[0].transitView[0]) ? '' : message
        }))
        if (!trackingProvider) fetchExceptions()
      }
    } catch (er:any) {
      console.error(
        'TRIP DETAILS::Fetching Transit History Error:',
        er,
        er.response
      )
      const { error, data, message } = responseParser(er.response)
      if (error) {
        setApiResponse({
          ...apiResponse,
          isSuccess: false,
          isError: true,
          isLoading: false,
          message
        })
      }
    }
  }

  async function fetchExceptions() {
    try {
      let queryParam = `?slug=${slug}`
      if (purifySearchParams(searchParams)) {
        queryParam = `${queryParam}&${purifySearchParams(searchParams)}`
      }
      const {
        data: { content }
      } = await get(`${config.tripExceptionURL}${queryParam}`, source.token)

      const data = content[0]
      setOtherFilters({
        ...otherFilters,
        exceptions: data.transitView[0].exceptions,
        stoppages: data.transitView[0].stoppages.filter(
          (item: any) => item.duration !== 0
        )
      })
    } catch (error) {
      console.error('App Error:', error)
    }
  }

  const prevSlug = React.useRef(slug)
  React.useEffect(() => {
    if (prevSlug.current !== slug) {
      hasData.current = false
      prevSlug.current = slug
    }
    if (!hasData.current) {
      setApiResponse(initialMapState)
    }
    fetchData()
  }, [slug, timer, trackingProvider])

  return (
    <TripDetailContext.Provider value={{ apiResponse, setApiResponse }}>
      {showDrawer ? (
        <IssueListing
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          resourceId={slug}
          config={issueRaisedConfig}
          resourceType="ConsignerTrip"
          baseUrl={''}
          updateIssueCount={() => {
            setApiResponse({
              ...apiResponse,
              data: {
                ...apiResponse.data,
                issuesCount: apiResponse.data.issuesCount + 1
              }
            })
          }}
          t={t}
        />
      ) : null}
      <RenderControl state={apiResponse} variant={'xl'}>
        <Grid
          gridTemplateRows={`${config.shareURL ? 40 : 80}px auto`}
          style={{ ...layoutStyle }}
          className={className}
        >
          <Header
            slug={slug}
            match={match}
            config={config}
            history={history}
            location={location}
            showTimezone={showTimezone}
            routesConfig={routesConfig}
            setIssueClicked={setShowDrawer}
          />
          <TripTabDetails
            data={{ ...apiResponse.data, ...otherFilters, tripPois }}
            mapKey={mapKey}
            aerisLayer={aerisLayer}
            defaultTab={tab as ITab}
            plant={plant !== null ? plant : ''}
            config={config.tripTabs}
            isShare={Boolean(config.shareURL)}
            match={match}
            history={history}
            location={location}
            onSelect={(x: any) => setSelectedIds({ ...x })}
            selectedIds={selectedIds}
            mapInfoToggle={config.mapOverInfoToggle}
            defaultBaseLayer={defaultBaseLayer}
            moveMarker={moveMarker}
            listItems={listItems}
            apiBasedDailyDistanceGraph={apiBasedDailyDistanceGraph}
            onTrackingProviderChange={setTrackingProvider}
            trackingProvider={trackingProvider}
          />
        </Grid>
      </RenderControl>
    </TripDetailContext.Provider>
  )
}
